// ============================================
// PESTANAINVENTARIO.JS — El Inventario ES La Tienda (2026-10-19)
// ============================================
// Principio de Ricardo: el inventario NO es una lista nueva. Es LA MISMA rejilla de tarjetas y los MISMOS filtros de
// La Tienda (rubro, familia, marca, busqueda, CCTV...), con la cara del almacen: existencia, disponible y apartado en
// vez de precio/margen. Aqui solo se pone encima el resumen del almacen y la entrada a mano (alta inicial, sobrante,
// devolucion, ajuste); todo lo demas es <Tienda modo="inventario" />, con su toggle Inventario <-> La Tienda.
// Las compras entran desde Compras; el material para un proyecto sale desde el expediente (apartar -> surtir).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { fetchAuth } from '../../utils/sesion';
import { API_BASE } from '../../config/api';
import { fmtDinero } from '../../utils/dinero';
import Tienda from '../../Tienda';
// SUB-ALMACENES (2026-10-19): esta pestana es CENTRAL (la bodega). Las herramientas viven aqui como activos.
import SeccionHerramientas from './SeccionHerramientas';
import SubirImagen from '../SubirImagen';

const PP = "'Poppins', sans-serif";
const COLOR_PRINCIPAL = '#5c9adb';
const COLOR_OSCURO = '#2f5f9e';
const COLOR_GRIS = '#4e5357';
const COLOR_GRIS_CLARO = '#8a8f95';
const COLOR_VERDE = '#1D9E75';

const FUENTES = {
  syscom_producto: { label: 'Syscom', color: '#5c9adb' },
  beep_producto:   { label: 'Mis Productos', color: '#5cd4b0' },
  articulo_interno: { label: 'Interno (no se vende)', color: '#7c5cdb' },
};
// 'compra' no se captura a mano: una compra entra confirmandola en Almacen > Compras.
const MOTIVOS_ENTRADA = [
  { id: 'alta_inicial', label: 'Alta inicial (lo que ya había)' },
  { id: 'sobrante',     label: 'Sobrante de obra' },
  { id: 'devolucion',   label: 'Devolución' },
  { id: 'ajuste',       label: 'Ajuste' },
];

// Cantidades (no dinero): hasta 4 decimales, sin ceros de relleno.
const fmtCant = (n) => Number(n || 0).toLocaleString('es-MX', { maximumFractionDigits: 4 });
const unidadCorta = (u) => (u === 'metro' ? 'm' : 'pza');

// Hook compartido: Almacen.js lo usa tambien.
export function useInventario() {
  const [existencias, setExistencias] = useState([]);
  const [veCostos, setVeCostos] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const recargar = useCallback(async () => {
    setCargando(true); setError('');
    try {
      const r = await fetchAuth(`${API_BASE}/api/inventario`);
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) throw new Error(d.error || 'No se pudo leer el inventario');
      setExistencias(d.existencias || []);
      setVeCostos(!!d.ve_costos);
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') setError(e.message);
    } finally {
      setCargando(false);
    }
  }, []);
  useEffect(() => { recargar(); }, [recargar]);
  return { existencias, veCostos, cargando, error, recargar };
}

// ============================================
// PESTANA: resumen + Entrada a mano + LA TIENDA en su cara de inventario.
// ============================================
export default function PestanaInventario({ inv, esCelular }) {
  const { existencias, veCostos, error, recargar } = inv;
  const [modal, setModal] = useState(null);
  const [aviso, setAviso] = useState('');
  const [tiendaKey, setTiendaKey] = useState(0); // para que la rejilla se vuelva a cargar tras una entrada

  // Central = productos del catalogo (los articulos internos tienen su propia pestana / seccion).
  const catalogo = useMemo(() => existencias.filter(e => e.tipo !== 'articulo_interno'), [existencias]);
  const totales = useMemo(() => ({
    productos: catalogo.length,
    conStock: catalogo.filter(e => Number(e.cantidad) > 0).length,
    piezas: catalogo.reduce((s, e) => s + (Number(e.cantidad) || 0), 0),
    apartado: catalogo.reduce((s, e) => s + (Number(e.apartado) || 0), 0),
    tecnicos: catalogo.reduce((s, e) => s + (Number(e.cantidad_tecnicos) || 0), 0),
    oficina: existencias.reduce((s, e) => s + (Number(e.cantidad_oficina) || 0), 0),
    instalado: catalogo.reduce((s, e) => s + (Number(e.cantidad_instalada) || 0), 0),
    costo: catalogo.reduce((s, e) => s + (Number(e.costo_total) || 0), 0),
  }), [catalogo, existencias]);

  const hecho = (msg) => { setModal(null); setAviso(msg); recargar(); setTiendaKey(k => k + 1); setTimeout(() => setAviso(''), 4000); };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ fontSize: 12, color: COLOR_GRIS_CLARO, lineHeight: 1.5, flex: 1, minWidth: 220 }}>
          <strong style={{ color: COLOR_GRIS }}>📦 Central (bodega)</strong> · {totales.productos} producto{totales.productos === 1 ? '' : 's'} · {totales.conStock} con existencia · {fmtCant(totales.piezas)} piezas
          {totales.apartado > 0 && <> · <strong style={{ color: '#b45309' }}>{fmtCant(totales.apartado)} apartadas</strong></>}
          {veCostos && totales.productos > 0 && <> · valor al costo promedio <strong style={{ color: COLOR_OSCURO }}>{fmtDinero(totales.costo)}</strong></>}
          {(totales.tecnicos > 0 || totales.oficina > 0 || totales.instalado > 0) && <> · fuera de Central: {totales.tecnicos > 0 ? `${fmtCant(totales.tecnicos)} con técnicos` : ''}{totales.oficina > 0 ? `${totales.tecnicos > 0 ? ', ' : ''}${fmtCant(totales.oficina)} en Oficina` : ''}{totales.instalado > 0 ? `${totales.tecnicos > 0 || totales.oficina > 0 ? ', ' : ''}${fmtCant(totales.instalado)} instaladas` : ''}</>}
          <br />Central <strong>es La Tienda</strong>: mismas tarjetas y filtros, con la cara del almacén (existencia · disponible · apartado). Las compras entran desde <strong>Compras</strong>; el material sale desde el <strong>expediente › Material</strong> (apartar → surtir a un técnico). Cada traspaso queda en el kardex.
        </div>
        <button onClick={() => setModal({ modo: 'entrada' })} style={{ padding: '10px 14px', minHeight: 44, background: COLOR_PRINCIPAL, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontFamily: PP, fontSize: 13, flexShrink: 0 }}>📥 Entrada (alta, sobrante, ajuste)</button>
      </div>
      {aviso && <div style={{ background: '#e8faf5', color: COLOR_VERDE, borderRadius: 10, padding: '10px 12px', fontSize: 12.5, fontWeight: 600 }}>{aviso}</div>}
      {error && <div style={{ background: '#fde8e8', color: '#b02020', borderRadius: 10, padding: '10px 12px', fontSize: 12.5 }}>{error}</div>}

      {/* HERRAMIENTAS (E): activos de Central, prestamo a tecnicos, "quien tiene el taladro" */}
      <SeccionHerramientas esCelular={esCelular} veCostos={veCostos} />

      {/* LA TIENDA, cara de inventario: la misma rejilla, los mismos filtros, el toggle para volver a precios. */}
      <div style={{ margin: esCelular ? '0 -12px' : 0, borderRadius: esCelular ? 0 : 12, overflow: 'hidden', border: esCelular ? 'none' : '1px solid #e5e8ec' }}>
        <Tienda key={tiendaKey} modo="inventario" />
      </div>

      {modal && modal.modo === 'entrada' && <ModalEntrada existencias={existencias} veCostos={veCostos} onCerrar={() => setModal(null)} onHecho={hecho} />}
    </div>
  );
}

// ============================================
// ARMAZON DE MODAL (celular primero): cabecera fija, cuerpo con scroll, pie fijo.
// ============================================
const overlay = { position: 'fixed', inset: 0, background: 'rgba(20,30,50,0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1200, padding: 12 };
const caja = { background: '#fff', borderRadius: 16, width: '100%', maxWidth: 480, maxHeight: 'calc(var(--beep-vh) * 92)', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(0,0,0,0.25)', fontFamily: PP, overflow: 'hidden' };
const cuerpo = { flex: 1, minHeight: 0, overflowY: 'auto', padding: '4px 20px 12px' };
const inputBase = { padding: '10px 12px', minHeight: 44, borderRadius: 10, border: '1px solid #d8dde3', fontFamily: PP, fontSize: 16, outline: 'none', width: '100%', boxSizing: 'border-box' };
const labelStyle = { fontSize: 11, color: COLOR_GRIS_CLARO, fontWeight: 700, marginBottom: 4, marginTop: 10, display: 'block', textTransform: 'uppercase' };

function Modal({ titulo, onCerrar, children, pie }) {
  return createPortal(
    <div style={overlay} onClick={onCerrar}>
      <div style={caja} onClick={(e) => e.stopPropagation()}>
        <div style={{ padding: '18px 20px 10px', fontSize: 17, fontWeight: 800, color: COLOR_OSCURO, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{titulo}</span>
          <button onClick={onCerrar} aria-label="Cerrar" style={{ border: 'none', background: 'transparent', fontSize: 20, cursor: 'pointer', color: COLOR_GRIS_CLARO, width: 36, height: 36 }}>×</button>
        </div>
        <div style={cuerpo}>{children}</div>
        {pie && <div style={{ padding: '10px 20px 16px', display: 'flex', gap: 8, borderTop: '1px solid #eef0f3' }}>{pie}</div>}
      </div>
    </div>,
    document.body
  );
}
const btnCancelar = { flex: 1, padding: 12, minHeight: 44, background: '#f1f3f6', border: 'none', borderRadius: 10, fontWeight: 700, cursor: 'pointer', fontFamily: PP };
const btnOk = (ok) => ({ flex: 1, padding: 12, minHeight: 44, background: COLOR_VERDE, color: '#fff', border: 'none', borderRadius: 10, fontWeight: 700, cursor: ok ? 'pointer' : 'default', fontFamily: PP, opacity: ok ? 1 : 0.5 });

function FilaProducto({ p, seleccionado, onClick, extra }) {
  const f = FUENTES[p.tipo] || FUENTES.beep_producto;
  return (
    <button type="button" onClick={onClick} style={{
      width: '100%', textAlign: 'left', display: 'flex', gap: 10, alignItems: 'center', padding: '8px 10px', minHeight: 48,
      background: seleccionado ? '#eef5fd' : '#fff', border: `1.5px solid ${seleccionado ? COLOR_PRINCIPAL : '#e5e8ec'}`, borderRadius: 10, cursor: 'pointer', fontFamily: PP,
    }}>
      <div style={{ width: 6, alignSelf: 'stretch', background: f.color, borderRadius: 3, flexShrink: 0 }} />
      {p.imagen_url && <img src={p.imagen_url} alt="" onError={(ev) => { ev.currentTarget.style.display = 'none'; }} style={{ width: 36, height: 36, objectFit: 'contain', borderRadius: 6, flexShrink: 0 }} />}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, color: COLOR_GRIS, overflowWrap: 'anywhere' }}>{p.nombre}</div>
        <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO }}>{f.label}{p.marca ? ` · ${p.marca}` : ''}{p.modelo ? ` · ${p.modelo}` : ''}</div>
      </div>
      {extra && <div style={{ fontSize: 12, fontWeight: 700, color: COLOR_OSCURO, flexShrink: 0 }}>{extra}</div>}
    </button>
  );
}

// ============================================
// ENTRADA a mano: buscar en el catalogo -> cantidad + costo -> motivo.
// ============================================
// destino: 'central' (default) u 'oficina' (SUB-ALMACENES D: consumibles que entran directo a Oficina).
// soloInternos: el buscador trae ARTICULOS INTERNOS (consumibles) en vez de La Tienda.
export function ModalEntrada({ existencias, veCostos, preseleccion, destino = 'central', soloInternos = false, onCerrar, onHecho }) {
  const [producto, setProducto] = useState(preseleccion ? { tipo: preseleccion.tipo, referencia_id: preseleccion.referencia_id, nombre: preseleccion.nombre, marca: preseleccion.marca, modelo: preseleccion.modelo, imagen_url: preseleccion.imagen_url } : null);
  const [q, setQ] = useState('');
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [cantidad, setCantidad] = useState('');
  const [costo, setCosto] = useState('');
  const [motivo, setMotivo] = useState((existencias || []).length === 0 ? 'alta_inicial' : 'sobrante');
  const [nota, setNota] = useState('');
  const [error, setError] = useState('');
  const [enviando, setEnviando] = useState(false);
  // ALMACEN v3 (A3): ningun producto entra sin imagen; si el servidor la pide, se toma/sube aqui mismo.
  const [faltaImagen, setFaltaImagen] = useState(false);
  const [imagenUrl, setImagenUrl] = useState('');

  useEffect(() => {
    const t = q.trim();
    if (t.length < 2) { setResultados([]); setBuscando(false); return undefined; }
    let vivo = true;
    setBuscando(true);
    const h = setTimeout(async () => {
      try {
        if (soloInternos) {
          const r = await fetchAuth(`${API_BASE}/api/inventario/articulos-internos?tipo=consumible&q=${encodeURIComponent(t)}`);
          const d = await r.json().catch(() => ({}));
          if (!vivo) return;
          setResultados(r.ok && d.ok ? (d.articulos || []).map(a => ({ tipo: 'articulo_interno', referencia_id: String(a.id), nombre: a.nombre, marca: a.marca || 'Interno', modelo: a.modelo || null, imagen_url: a.imagen_url || null, costo_catalogo: a.costo_promedio != null ? a.costo_promedio : null })) : []);
          return;
        }
        const r = await fetchAuth(`${API_BASE}/api/tienda/buscar?limit=12&q=${encodeURIComponent(t)}`);
        const d = await r.json().catch(() => ({}));
        if (!vivo) return;
        setResultados(r.ok && d.ok ? (d.productos || []).map(p => ({
          tipo: p.tipo === 'syscom_producto' ? 'syscom_producto' : 'beep_producto',
          referencia_id: String(p.referencia_id),
          nombre: p.nombre, marca: p.marca || null, modelo: p.modelo || null, imagen_url: p.imagen_url || null,
          costo_catalogo: p.costo_mxn != null ? p.costo_mxn : null,
        })).filter(p => p.tipo === 'syscom_producto' || /^\d+$/.test(p.referencia_id)) : []);
      } catch (e) {
        if (vivo && e.code !== 'SESION_EXPIRADA') setResultados([]);
      } finally {
        if (vivo) setBuscando(false);
      }
    }, 300);
    return () => { vivo = false; clearTimeout(h); };
  }, [q, soloInternos]);

  const yaEnInventario = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (t.length < 2) return [];
    return (existencias || []).filter(e => `${e.nombre} ${e.marca || ''} ${e.modelo || ''}`.toLowerCase().includes(t)).slice(0, 5);
  }, [q, existencias]);

  const elegir = (p) => {
    setProducto(p);
    if (veCostos && p.costo_catalogo != null && costo === '') setCosto(String(p.costo_catalogo));
  };

  const cantOk = /^\d+(\.\d+)?$/.test(cantidad.trim()) && Number(cantidad) > 0;
  const costoOk = veCostos ? /^\d+(\.\d+)?$/.test(costo.trim().replace(/,/g, '')) : true;
  const listo = producto && cantOk && costoOk && !enviando;

  const confirmar = async () => {
    if (!listo) return;
    setEnviando(true); setError('');
    try {
      const r = await fetchAuth(`${API_BASE}/api/inventario/entradas`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tipo: producto.tipo, referencia_id: producto.referencia_id, cantidad: cantidad.trim(), ...(veCostos ? { costo_unitario: costo.trim().replace(/,/g, '') } : {}), motivo, nota: nota.trim() || null, destino, imagen_url: imagenUrl || null, requiere_serie: false }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok || !d.ok) { if (d.falta_imagen) setFaltaImagen(true); throw new Error(d.error || 'No se pudo registrar la entrada'); }
      onHecho(destino === 'oficina'
        ? `Entrada registrada en Oficina: ${fmtCant(cantidad)} ${unidadCorta(d.existencia && d.existencia.unidad)} de "${producto.nombre}".`
        : `Entrada registrada: ${fmtCant(cantidad)} ${unidadCorta(d.existencia && d.existencia.unidad)} de "${producto.nombre}". Existencia en Central: ${fmtCant(d.existencia.cantidad)}.`);
    } catch (e) {
      if (e.code !== 'SESION_EXPIRADA') setError(e.message);
      setEnviando(false);
    }
  };

  return (
    <Modal titulo={destino === 'oficina' ? '📥 Entrada a Oficina' : '📥 Registrar entrada'} onCerrar={onCerrar} pie={<>
      <button onClick={onCerrar} style={btnCancelar}>Cancelar</button>
      <button onClick={confirmar} disabled={!listo} style={btnOk(listo)}>{enviando ? 'Guardando…' : 'Confirmar entrada'}</button>
    </>}>
      <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4, lineHeight: 1.5 }}>{destino === 'oficina'
        ? <>Consumibles que entran directo a Oficina (quedan en el kardex). Si lo que entra es material de proyecto, va a Central.</>
        : <>Para lo que ya había, sobrantes de obra, devoluciones o ajustes. <strong>Las compras no se capturan aquí</strong>: se confirman en Compras y entran solas con su costo real y su garantía del proveedor.</>}</div>
      {!producto ? (
        <>
          <label style={labelStyle}>{soloInternos ? 'Consumible (artículo interno)' : 'Producto del catálogo'}</label>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} style={inputBase} placeholder={soloInternos ? 'Papel, café, tóner…' : 'Modelo, nombre o marca…'} inputMode="search" enterKeyHint="search" autoComplete="off" />
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 6 }}>
            {q.trim().length < 2 ? (soloInternos ? 'Escribe al menos 2 letras. Solo consumibles dados de alta en Oficina.' : 'Escribe al menos 2 letras. Solo se puede dar de alta lo que existe en el catálogo (Syscom o Mis Productos).') : buscando ? 'Buscando…' : `${resultados.length} resultado${resultados.length === 1 ? '' : 's'}`}
          </div>
          {yaEnInventario.length > 0 && (
            <>
              <div style={{ ...labelStyle, color: COLOR_VERDE }}>Ya en el inventario</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {yaEnInventario.map(e => <FilaProducto key={`inv-${e.id}`} p={e} onClick={() => elegir({ tipo: e.tipo, referencia_id: e.referencia_id, nombre: e.nombre, marca: e.marca, modelo: e.modelo, imagen_url: e.imagen_url })} extra={`${fmtCant(e.cantidad)} ${unidadCorta(e.unidad)}`} />)}
              </div>
            </>
          )}
          {resultados.length > 0 && (
            <>
              <div style={labelStyle}>Catálogo</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {resultados.map(p => <FilaProducto key={`${p.tipo}-${p.referencia_id}`} p={p} onClick={() => elegir(p)} />)}
              </div>
            </>
          )}
        </>
      ) : (
        <>
          <label style={labelStyle}>Producto</label>
          <FilaProducto p={producto} seleccionado onClick={() => { if (!preseleccion) { setProducto(null); setCosto(''); } }} extra={preseleccion ? null : 'cambiar'} />
          <div style={{ display: 'flex', gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={labelStyle}>Cantidad</label>
              <input type="text" inputMode="decimal" value={cantidad} onChange={(e) => setCantidad(e.target.value)} style={inputBase} placeholder="0" autoFocus />
            </div>
            {veCostos && (
              <div style={{ flex: 1 }}>
                <label style={labelStyle}>Costo unitario (MXN)</label>
                <input type="text" inputMode="decimal" value={costo} onChange={(e) => setCosto(e.target.value)} style={inputBase} placeholder="0.00" />
              </div>
            )}
          </div>
          <div style={{ fontSize: 11, color: COLOR_GRIS_CLARO, marginTop: 4 }}>
            {veCostos
              ? 'El costo se guarda tal cual, con todos sus decimales; el promedio lo calcula el sistema sin redondear.'
              : 'Entra al costo promedio vigente del producto. La primera entrada de un producto nuevo la registra compras o dirección.'}
          </div>
          <label style={labelStyle}>Motivo</label>
          <select value={motivo} onChange={(e) => setMotivo(e.target.value)} style={inputBase}>
            {MOTIVOS_ENTRADA.map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </select>
          <label style={labelStyle}>Nota (opcional)</label>
          <input value={nota} onChange={(e) => setNota(e.target.value)} style={inputBase} placeholder="De dónde viene, obra, observaciones…" />
          {(faltaImagen || (producto && !producto.imagen_url)) && !imagenUrl && (
            <div style={{ marginTop: 10, background: '#fffbeb', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontSize: 12.5, color: '#b45309', fontWeight: 700 }}>📷 Sin imagen: toma una foto o sube la del proveedor (ningún producto entra sin imagen).</div>
              <SubirImagen folder="beep-os/almacen" aspectRatio="square" label="" capture="environment" onChange={(url) => { setImagenUrl(url); setFaltaImagen(false); setError(''); }} />
            </div>
          )}
          {imagenUrl && <div style={{ fontSize: 12, color: COLOR_VERDE, marginTop: 6 }}>Imagen lista.</div>}
        </>
      )}
      {error && <div style={{ marginTop: 10, background: '#fde8e8', color: '#b02020', borderRadius: 10, padding: '8px 10px', fontSize: 12 }}>{error}</div>}
    </Modal>
  );
}
